import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ConversationBillingFilter } from '@/utils/conversationBilling';

/**
 * The conversation list's state, held in the URL rather than in component
 * state.
 *
 * Opening a conversation and pressing Back used to drop you on page 1 with
 * every filter cleared: the page number lived in ConversationTable's own
 * useState, Back was a fresh navigation to `?tab=conversations`, and there was
 * nothing to restore from. In the URL it survives Back, a reload, and a link
 * someone pastes to a colleague.
 *
 * Params are omitted when they hold their default, so an untouched list has a
 * clean `?tab=conversations` and does not accumulate noise.
 */
export interface ConversationListParams {
  page: number;
  search: string;
  billing: ConversationBillingFilter;
  /**
   * Only calls with an open callback request. Separate from `billing` because
   * they are different dimensions -- a call can both count towards usage and
   * still be waiting for someone to ring back.
   */
  callback: boolean;
  dateRange: { from: Date | undefined; to: Date | undefined };
}

const BILLING_VALUES: ConversationBillingFilter[] = [
  'all',
  'billable',
  'non_billable',
];

const parseDate = (value: string | null): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/** `yyyy-MM-dd`, so the URL stays readable and sortable. */
const formatDate = (date: Date | undefined): string | null => {
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

export function useConversationListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<ConversationListParams>(() => {
    const rawPage = Number(searchParams.get('page'));
    const rawBilling = searchParams.get('billing') as ConversationBillingFilter;

    return {
      page: Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 1,
      search: searchParams.get('q') ?? '',
      billing: BILLING_VALUES.includes(rawBilling) ? rawBilling : 'all',
      callback: searchParams.get('callback') === '1',
      dateRange: {
        from: parseDate(searchParams.get('from')),
        to: parseDate(searchParams.get('to')),
      },
    };
  }, [searchParams]);

  const update = useCallback(
    (next: Partial<ConversationListParams>) => {
      setSearchParams(
        current => {
          const merged = new URLSearchParams(current);

          const set = (key: string, value: string | null) => {
            if (value == null || value === '') merged.delete(key);
            else merged.set(key, value);
          };

          if (next.search !== undefined) set('q', next.search.trim());
          if (next.billing !== undefined)
            set('billing', next.billing === 'all' ? null : next.billing);
          if (next.callback !== undefined)
            set('callback', next.callback ? '1' : null);
          if (next.dateRange !== undefined) {
            set('from', formatDate(next.dateRange.from));
            set('to', formatDate(next.dateRange.to));
          }

          // Any change to what is being listed invalidates the page number:
          // page 4 of an unfiltered list is rarely page 4 of a filtered one.
          // An explicit page in the same call still wins.
          if (
            next.search !== undefined ||
            next.billing !== undefined ||
            next.callback !== undefined ||
            next.dateRange !== undefined
          ) {
            merged.delete('page');
          }
          if (next.page !== undefined)
            set('page', next.page > 1 ? String(next.page) : null);

          return merged;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { params, update };
}

export default useConversationListParams;
